/**
 * Open trades strip under the account bar: lists the assets the client has open positions in on
 * their live (real money) account, whichever of Demo / Live is selected; demo trades never appear.
 * With no live open trades the line stays in place, empty, so the layout doesn't jump. One live price per traded asset, in the order the positions were opened, filling the width it
 * has. Clicking an asset opens its chart. Only when the line is full does a +N button appear at
 * the right end, listing the assets that did not fit.
 */
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { fmt, fmtPct, type Position, type UserEvent } from '@platform/shared';
import { api } from '../lib/api';
import { navigate, usePath } from '../lib/router';
import { useStore } from '../lib/store';
import { ws } from '../lib/ws';
import { useInstrument, useTicker } from '../stores/market';
import { sessionStore, useActiveAccount } from '../stores/session';
import { usePositions } from '../stores/trading';
import { Change, cx } from './ui';
import { InstrumentIcon } from './InstrumentIcon';

/** The footer's mini price row. */
export const STRIP_SYMBOLS = ['BTC', 'ETH', 'SOL', 'GOLD', 'OIL', 'AAPL'];

/** Room kept for the +N button and its gap until it has been drawn and measured (px). */
const MORE_FALLBACK = 64;

function Price({ sym }: { sym: string }) {
  const t = useTicker(sym);
  const inst = useInstrument(sym);
  return (
    <>
      <span className="num text-muted">{t ? fmt(t.mark, inst?.priceDecimals ?? 2) : '—'}</span>
      <Change pct={t?.chg24}>{t ? fmtPct(t.chg24) : ''}</Change>
    </>
  );
}

/** One asset in the line. Items past the fit stay in the DOM (invisible and inert) so they keep a measurable width. */
function Item({ sym, shown, itemRef }: { sym: string; shown: boolean; itemRef: (el: HTMLElement | null) => void }) {
  return (
    <span ref={itemRef} inert={!shown} className={cx('flex shrink-0 items-center gap-1', !shown && 'invisible')}>
      <button type="button" onClick={() => navigate(`/trade/${sym}`)} className="flex items-center gap-1.5 bg-transparent text-fg">
        <InstrumentIcon symbol={sym} size={16} />
        <span className="font-semibold">{sym}</span>
        <Price sym={sym} />
      </button>
    </span>
  );
}

/** The +N button at the right end and its menu of the assets that did not fit. */
function More({ symbols, boxRef }: { symbols: string[]; boxRef: RefObject<HTMLDivElement | null> }) {
  const path = usePath();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, boxRef]);
  useEffect(() => setOpen(false), [path]);
  return (
    <div ref={boxRef} className="relative ml-7 shrink-0">
      <button
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${symbols.length} more assets: ${symbols.join(', ')}`}
        title={symbols.join(', ')}
        onClick={() => setOpen((o) => !o)}
        className={cx('flex items-center gap-1 rounded bg-transparent px-1 transition-colors hover:text-fg', open ? 'text-fg' : 'text-muted')}
      >
        <span className="text-[15px] leading-none">+</span>
        <span className="num">{symbols.length}</span>
      </button>
      {open && (
        <div role="menu" aria-label="More assets" className="shadow-pop absolute top-full right-0 z-40 mt-1.5 w-56 rounded-xl border border-line bg-panel p-1.5 text-fg">
          {symbols.map((s) => (
            <button
              key={s}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate(`/trade/${s}`);
              }}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-raised"
            >
              <InstrumentIcon symbol={s} size={16} />
              <span className="font-semibold">{s}</span>
              <span className="ml-auto flex items-center gap-1.5">
                <Price sym={s} />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Open positions on the live account. While Live is selected they come from the trading store;
 *  while Demo is selected they are loaded here and kept current from the live account's channel. */
function useLiveOpenPositions(): Position[] {
  const liveId = useStore(sessionStore, (s) => s.me?.accounts.find((a) => a.kind === 'live')?.id ?? null);
  const active = useActiveAccount();
  const activeIsLive = !!liveId && active?.id === liveId;
  const storePositions = usePositions();
  const [own, setOwn] = useState<Position[]>([]);
  useEffect(() => {
    setOwn([]);
    if (!liveId || activeIsLive) return;
    let alive = true;
    const load = () =>
      api
        .get<Position[]>(`/api/positions?accountId=${liveId}&status=open`)
        .then((ps) => alive && setOwn(ps))
        .catch(() => {});
    void load();
    const unsub = ws.subscribe(`user:${liveId}`, (ev: UserEvent) => {
      if (ev.type !== 'position') return;
      const p = ev.position;
      setOwn((ps) => (p.status === 'open' ? [p, ...ps.filter((x) => x.id !== p.id)] : ps.filter((x) => x.id !== p.id)));
    });
    // Periodic resync covers anything missed during a reconnect.
    const t = window.setInterval(load, 60_000);
    return () => {
      alive = false;
      unsub();
      window.clearInterval(t);
    };
  }, [liveId, activeIsLive]);
  if (!liveId) return [];
  return activeIsLive ? storePositions : own;
}

export function TickerStrip() {
  const positions = useLiveOpenPositions();
  // Positions arrive newest first; list each asset once, oldest trade first.
  const symbols = [...new Set(positions.map((p) => p.symbol).reverse())];
  return <OpenTradesLine candidates={symbols} />;
}

function OpenTradesLine({ candidates }: { candidates: string[] }) {
  const candidateKey = candidates.join(',');

  // How many whole items fit on the line; starts optimistic and is settled before the first paint.
  const [shown, setShown] = useState(candidates.length);
  const outer = useRef<HTMLDivElement>(null);
  const row = useRef<HTMLDivElement>(null);
  const more = useRef<HTMLDivElement>(null);
  const items = useRef(new Map<string, HTMLElement>());

  const measure = () => {
    const box = outer.current;
    const line = row.current;
    if (!box || !line) return;
    const cs = getComputedStyle(box);
    const full = box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const gap = parseFloat(getComputedStyle(line).columnGap) || 28;
    const widths = candidates.map((s) => items.current.get(s)?.offsetWidth ?? 0);
    const span = (k: number) => widths.slice(0, k).reduce((a, w) => a + w, 0) + Math.max(0, k - 1) * gap;
    let k = candidates.length;
    if (span(k) > full) {
      const reserve = more.current ? more.current.offsetWidth + gap : MORE_FALLBACK;
      k = 0;
      while (k < candidates.length && span(k + 1) + reserve <= full) k++;
    }
    setShown((prev) => (prev === k ? prev : k));
  };
  // Every render: the reads are a dozen widths, and the one extra pass after the +N button mounts
  // (so its real width is known) happens before paint.
  useLayoutEffect(measure);
  useEffect(() => {
    const box = outer.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(box);
    // Items change width when a price gains or loses a digit.
    for (const el of items.current.values()) ro.observe(el);
    return () => ro.disconnect();
  }, [candidateKey]);

  const overflow = candidates.slice(shown);
  return (
    <div ref={outer} className="flex items-center border-b border-line px-4 py-1.5 text-xs md:px-5">
      <div ref={row} className="flex min-h-4 min-w-0 flex-1 items-center gap-x-7 overflow-hidden">
        {candidates.map((s, i) => (
          <Item
            key={s}
            sym={s}
            shown={i < shown}
            itemRef={(el) => {
              if (el) items.current.set(s, el);
              else items.current.delete(s);
            }}
          />
        ))}
      </div>
      {overflow.length > 0 && <More symbols={overflow} boxRef={more} />}
    </div>
  );
}
